import React, { FC, useCallback } from "react"
import styled, { css } from "styled-components"
import { TestableComponent } from "../../e2e-tests/utils/locator-utils"
import { assertNever } from "../../typings/typeUtils"
import { BrandColor, theme } from "../../util/styles/theme"
import { Icon } from "../icon/Icon"

export type TagId = string | number

export interface TagItem {
  id: TagId
  text: string
}

export const tagVariants = [
  "green",
  "greyCold",
  "orange",
  "red",
  "yellow",
] as const

export type TagVariant = typeof tagVariants[number]

const DEFAULT_VARIANT: TagVariant = "yellow"
export const tagSizes = ["xsmall", "small", "medium"] as const

export type TagSize = typeof tagSizes[number]
const DEFAULT_SIZE: TagSize = "medium"

const colorsByVariant: Record<
  TagVariant,
  { color: BrandColor; backgroundColor: BrandColor }
> = {
  green: { color: "white", backgroundColor: "green" },
  greyCold: { color: "primary", backgroundColor: "greyCold" },
  orange: { color: "white", backgroundColor: "orange" },
  red: { color: "white", backgroundColor: "red" },
  yellow: { color: "primary", backgroundColor: "yellow" },
}

export interface TagProps {
  className?: string
  onDelete?: (tagId: TagId) => void
  tag: TagItem
  variant?: TagVariant
  size?: TagSize
}

export type TagStyleProps = Required<Pick<TagProps, "variant" | "size">>
export const TagStyle = css<TagStyleProps>`
  color: ${({ variant }) =>
    theme.colors[colorsByVariant[variant as TagVariant].color]};
  background-color: ${({ variant }) =>
    theme.colors[colorsByVariant[variant].backgroundColor]};

  @media print {
    color: ${theme.colors.primary};
    background-color: ${theme.colors.greyLight};
  }

  height: fit-content;
  ${({ size }) => {
    switch (size) {
      case "xsmall":
        return css`
          ${theme.textStyle("default", "xsmall", "normal")};
          border-radius: ${theme.borderRadius[size]};
          padding: ${theme.spacings.xsmall} ${theme.spacings.small};
        `
      case "small":
        return css`
          ${theme.textStyle("default", "small", "medium")};
          border-radius: ${theme.borderRadius[size]};
          padding: ${theme.spacings.xsmall} ${theme.spacings.small};
        `
      case "medium":
        return css`
          ${theme.textStyle("default", "small", "semibold")};
          border-radius: ${theme.borderRadius[size]};
          padding: ${theme.spacings.small} ${theme.spacings.medium};
        `
      default:
        return assertNever(size)
    }
  }}
`

const Container = styled.span<Required<Pick<TagProps, "variant" | "size">>>`
  ${TagStyle}
`

const CrossIcon = styled(Icon).attrs({ icon: "cross" })`
  ${theme.textStyle("default", "large", "semibold")}
`

const DeleteButton = styled.button<Required<Pick<TagProps, "variant">>>`
  background: none;
  border: none;
  color: ${({ variant }) =>
    theme.colors[colorsByVariant[variant as TagVariant].color]};
  fill: ${({ variant }) =>
    theme.colors[colorsByVariant[variant as TagVariant].color]};
  cursor: pointer;
  margin-right: -${theme.spacings.medium};
  vertical-align: middle;
`

export const Tag: FC<TagProps> & TestableComponent = ({
  className,
  tag,
  onDelete,
  variant = DEFAULT_VARIANT,
  size = DEFAULT_SIZE,
}) => {
  const handleClick = useCallback(() => {
    onDelete?.(tag.id)
  }, [onDelete, tag])

  return (
    <Container
      className={`${Tag.testClassName} ${className ?? ""}`}
      variant={variant}
      size={size}
    >
      <span>{tag.text}</span>
      {onDelete && (
        <DeleteButton type="button" onClick={handleClick} variant={variant}>
          <CrossIcon />
        </DeleteButton>
      )}
    </Container>
  )
}

Tag.defaultProps = {
  variant: DEFAULT_VARIANT,
  size: DEFAULT_SIZE,
}

Tag.testClassName = "tag"
